<template>
  <div class="account">
    <div class="row">
      <div class="col-md-12">
        <h4>Account Details</h4>
        <p>
          <strong>Member: </strong>
          <router-link :to="`/membership/members/${account.uuid}`">
            {{ account.dropdownLabel }} -
            <strong>{{ account.AccountType.name }}</strong>
          </router-link>
        </p>
        <p>
          <strong>Membership Status: </strong>
          <span class="label" :class="membershipStatus.label">
            {{ membershipStatus.text }}
          </span>
        </p>
        <template v-if="account.businessName">
          <p><strong>Business Name: </strong>{{ account.businessName }}</p>
        </template>
        <p>
          <strong>Smartcard:</strong>
          <span class="label label-margin" :class="{
            'label-danger': !account.cardId,
            'label-success': account.cardId
          }">
            <span class="fa fa-id-card"></span>
            {{ !account.cardId ? 'No Smartcard Assigned' : account.Smartcard.cardNumber }}
          </span>
        </p>
        <p v-if="account.MemberDiscount">
          <strong>Member Discount:</strong>
          <span class="label label-primary label-margin">
            <strong>{{ account.MemberDiscount.percentDiscount }} %</strong>
          </span>
        </p>
        <p>
          <strong>Balance</strong>
          <span :class="{ 'text-danger' : account.credits < 0 }">
            <strong>{{ account.credits | to_dollar_amount_neg }}</strong>
          </span>
        </p>
        <p>
          <strong>Registered Date</strong>
          {{ account.registeredDate | short_date_format }}
        </p>
        <p>
          <strong>Debt Date</strong>
          {{ account.negativeDate | short_date_format }}
        </p>
        <p><strong>Address:</strong>
          {{ account.address }} {{ account.suburb }}
          {{ account.state }} {{ account.postcode }}
        </p>
        <p><strong>Date of Birth:</strong>
          {{ account.dateOfBirth | short_date_format }}
        </p>
        <div class="alert" :class="{
          'alert-danger': !account.CreditCards.length,
          'alert-success': account.CreditCards.length
        }">
          {{ !account.CreditCards.length ? 'No credit card on account.' : 'Has credit card on account.' }}
        </div>
        <div v-if="account.MasterUuid" class="alert" :class="{
          'alert-danger': !account.Master.CreditCards.length,
          'alert-success': account.Master.CreditCards.length
        }">
          {{ !account.Master.CreditCards.length ? 'Master account does not have a credit card on file.' : 'Master account has credit card on file.' }}
        </div>
        <template v-if="account.isMasterAccount">
          <h4>Linked Drivers</h4>
          <template v-for="driver in account.Drivers">
            <p :key="driver.uuid">
              <router-link :to="{ path: '/membership/members/' + driver.uuid }">
                {{ driver.dropdownLabel }}
              </router-link>
            </p>
          </template>
          <p>
            <strong>Allowed Drivers:</strong>
            {{ account.AccountType.maxAccounts }}
          </p>
        </template>
        <template v-if="!account.isMasterAccount && account.Master">
          <h4>Master Account</h4>
          <p>
            <router-link :to="{ path: '/membership/members/' + account.Master.uuid }">
              {{ account.Master.dropdownLabel }}
            </router-link>
          </p>
        </template>
      </div>
    </div>
    <div class="toolbar text-right">
      <router-link class="btn btn-default" :to="{ path: `/membership/members/edit/${account.uuid}` }">
        <span class="fa fa-edit fa-fw"></span> Edit
      </router-link>
      <router-link class="btn btn-default" :to="{ path: `/membership/members/${account.uuid}` }">
        <span class="fa fa-search fa-fw"></span> View
      </router-link>
      <dropdown-input
        label="Account Actions"
        label-icon="fa-user"
        :dropup="true"
        :actions="userActions(account, loggedInUser)"
        @action="executeAction($event, account)">
      </dropdown-input>
    </div>
  </div>
</template>

<script>
import AccountNoteDisplay from '@/components/displays/AccountNoteDisplay'
import ReservationDisplay from '@/components/displays/ReservationDisplay'
import DropdownInput from '@/components/inputs/DropdownInput'
import {
  ADD_ACCOUNT_NOTE,
  ADD_CREDITS,
  ADD_SMARTCARD,
  CHANGE_PASS_USER,
  CHANGE_PLAN_USER,
  EDIT_APPLICATION,
  EDIT_SMARTCARD,
  GENERATE_MONTHLY_INVOICE,
  INVITE_DRIVER,
  LOGIN_AS_USER,
  MAKE_ACTIVE,
  MAKE_INACTIVE,
  NEW_RESERVATION_USER,
  QUIT_MEMBER,
  REJOIN_MEMBER,
  RESTRICT_MEMBER,
  BLACKLIST_MEMBER,
  UN_BLACKLIST_MEMBER,
  UNASSIGN_SMARTCARD,
  UNRESTRICT_MEMBER
} from '@/vuex/types'
import { mapGetters, mapActions } from 'vuex'

export default {
  components: {
    AccountNoteDisplay,
    DropdownInput,
    ReservationDisplay
  },
  name: 'Account',
  props: {
    object: {
      type: Object
    },
    isFinanceAdmin: {
      type: Boolean,
      default () {
        return false
      }
    }
  },
  computed: {
    account () {
      return {
        AccountType: {},
        DriversLicense: {},
        DriversLicenseBack: {},
        Reservations: [],
        AccountNotes: [],
        CreditCards: [],
        ...this.object,
        ...this.updatedAccount
      }
    },
    membershipStatus () {
      let label = ''
      let text = ''
      let {
        activated,
        restricted,
        memberQuit
      } = this.account
      if (memberQuit) {
        label = 'label-warning'
        text = 'Quit'
      } else if (restricted) {
        label = 'label-danger'
        text = 'Restricted'
      } else if (!activated) {
        label = 'label-default'
        text = 'Inactive'
      } else {
        label = 'label-success'
        text = 'Active'
      }
      return { label, text }
    },
    ...mapGetters([
      'userActions',
      'loggedInUser'
    ])
  },
  data () {
    return {
      updatedAccount: {},
      url: 'https://techcar-dev.s3.amazonaws.com/'
    }
  },
  methods: {
    async executeAction (action, payload) {
      let result = await this[action](payload)
      if (result && result.updated) this.updatedAccount = result.updated
    },
    ...mapActions([
      ADD_ACCOUNT_NOTE,
      ADD_CREDITS,
      ADD_SMARTCARD,
      BLACKLIST_MEMBER,
      UN_BLACKLIST_MEMBER,
      CHANGE_PASS_USER,
      CHANGE_PLAN_USER,
      EDIT_APPLICATION,
      EDIT_SMARTCARD,
      GENERATE_MONTHLY_INVOICE,
      INVITE_DRIVER,
      LOGIN_AS_USER,
      MAKE_ACTIVE,
      MAKE_INACTIVE,
      NEW_RESERVATION_USER,
      QUIT_MEMBER,
      REJOIN_MEMBER,
      RESTRICT_MEMBER,
      UNASSIGN_SMARTCARD,
      UNRESTRICT_MEMBER
    ])
  }
}
</script>



// WEBPACK FOOTER //
// src/components/cards/NegativeAccount.vue